import { render } from './app.js';
import { contratoDe, filtrarPrecios, importeDe, pedidoDe, precioDe, usuarioDe } from './dominio.js';
import { S } from './estado.js';
import { anioActual, cant, eur, fecha, hoyISO, num, val } from './formato.js';
import { toast } from './ui.js';
import { db } from '../data/db.js';
import { supabase } from '../data/supabase.js';
import { sugerirTipo } from '../features/ficheros.js';
import { descargarPlantilla } from '../features/importar-precios.js';
import { filaLineaPedido, filaResultadoBusqueda, lineaImputacionHTML } from '../modales/modales.js';

/* Un <input type="date"> dispara 'change' en cuanto la fecha queda "completa",
   lo que puede pasar con el propio campo todavía enfocado (p.ej. al escribir
   el primer dígito del año, con día y mes ya puestos). Si repintamos ahí, el
   campo se reconstruye y el cursor vuelve siempre al día, así que el resto de
   dígitos que sigues tecleando caen sobre el día en vez de sobre el año.
   Si el campo sigue con el foco, esperamos a que lo sueltes de verdad. */
function renderTrasEditar(el){
  if(document.activeElement===el) el.addEventListener('blur', render, {once:true});
  else render();
}

/* La fecha de cada línea se teclea en tres cajas (dd/mm/aa) para poder poner
   el año con 2 dígitos. Si está toda vacía es que es una línea nueva: hoy. */
function fechaDeFila(fila){
  const d = fila.querySelector('.li-d').value, m = fila.querySelector('.li-m').value, a = fila.querySelector('.li-a').value;
  if(!d && !m && !a) return hoyISO();
  const yyyy = a ? '20'+a.padStart(2,'0') : String(anioActual);
  const iso = `${yyyy}-${(m||'01').padStart(2,'0')}-${(d||'01').padStart(2,'0')}`;
  return iso>hoyISO() ? hoyISO() : iso;
}

/* PK de inicio y fin en dos cajas; si coinciden (o no hay fin) se guarda como
   un punto kilométrico único, igual que antes de separarlas. */
function pkDeFila(fila){
  const ini = fila.querySelector('.li-pki').value.trim(), fin = fila.querySelector('.li-pkf').value.trim();
  if(!ini) return '—';
  return fin && fin!==ini ? `${ini} a ${fin}` : ini;
}

/* La tabla de líneas del pedido vive solo en el DOM (igual que las líneas
   de imputación): no hay estado en S, así que recalcular importe y total
   es leer directamente las filas, sin pasar por render(). */
function actualizarLineaPedido(inputCant){
  const fila = inputCant.closest('tr'), precio = precioDe(+fila.dataset.precio);
  const cnt = Math.max(0, parseFloat(inputCant.value)||0);
  fila.querySelector('.lp-importe').textContent = eur(precio.precio*cnt);
}
function actualizarTotalPedido(){
  const total = [...document.querySelectorAll('#lineas tr[data-precio]')].reduce((s,fila)=>{
    const precio = precioDe(+fila.dataset.precio), cnt = parseFloat(fila.querySelector('.lp-cant').value)||0;
    return s + precio.precio*cnt;
  }, 0);
  const el = document.getElementById('p-total');
  if(el) el.textContent = eur(total);
}

/* Orden de tabulación por Intro dentro de una línea de imputación: al llegar
   al final (observaciones) se añade una línea nueva en vez de saltar a nada. */
const ORDEN_LINEA_IMP = ['li-busca','li-d','li-m','li-a','li-cant','li-carr','li-pki','li-pkf','li-obs'];

/* Trae los perfiles de Supabase a db.usuarios, con sus contratos asignados
   resueltos en la misma consulta. RLS decide cuántas filas llegan: un admin
   las ve todas, cualquier otro solo la suya (que es justo lo que hace falta
   para poder construir S.usuario justo después de iniciar sesión). */
async function cargarUsuarios(){
  const { data, error } = await supabase.from('perfiles').select('*, usuario_contrato(contrato)');
  if(error){ db.usuarios=[]; return; }
  db.usuarios = data.map(p=>({id:p.id, nombre:p.nombre, email:p.email, rol:p.rol, area:p.area, activo:p.activo,
    contratos: p.todos_contratos ? 'todos' : p.usuario_contrato.map(x=>x.contrato)}));
}

/* Trae un catálogo (zonas/carreteras/almacenes) tal cual, ordenado por
   nombre — son solo listas para desplegables. */
async function cargarCatalogo(tipo){
  const { data } = await supabase.from(tipo).select('*').order('nombre');
  db[tipo] = data||[];
}

async function cargarAjustes(){
  const { data } = await supabase.from('ajustes').select('*').single();
  if(data) db.ajustes = {iva:data.iva, beneficioIndustrial:data.beneficio_industrial, gastosGenerales:data.gastos_generales};
}

/* RLS ya filtra qué contratos llegan (todos para un admin, solo los
   asignados para el resto), así que no hace falta repetir ese filtro aquí. */
async function cargarContratos(){
  const { data, error } = await supabase.from('contratos').select('*, anualidades(anio, importe)').order('expediente');
  if(error){ db.contratos=[]; return; }
  db.contratos = data.map(c=>({...c, gastosGenerales:c.gastos_generales, beneficioIndustrial:c.beneficio_industrial,
    anualidades:c.anualidades||[]}));
}

/* La columna se llama "descripcion" en la tabla (desc es palabra reservada
   en SQL); en la app siempre se ha llamado "desc", así que se traduce aquí. */
async function cargarPrecios(){
  const { data, error } = await supabase.from('precios').select('*').order('codigo');
  if(error){ db.precios=[]; return; }
  db.precios = data.map(p=>({id:p.id, contrato:p.contrato, codigo:p.codigo, desc:p.descripcion, ud:p.ud, precio:p.precio, capitulo:p.capitulo}));
}

/* observaciones→obs, autor→usuario y precio_unitario→precioUnitario, para
   no tocar el resto de la app que ya usa esos nombres desde el prototipo. */
async function cargarImputaciones(){
  const { data, error } = await supabase.from('imputaciones').select('*').order('fecha', {ascending:false});
  if(error){ db.imputaciones=[]; return; }
  db.imputaciones = data.map(i=>({id:i.id, contrato:i.contrato, precio:i.precio, precioUnitario:i.precio_unitario,
    cantidad:i.cantidad, fecha:i.fecha, carretera:i.carretera, pk:i.pk, destino:i.destino, obs:i.observaciones,
    estado:i.estado, usuario:i.autor, pedido:i.pedido, certificacion:i.certificacion}));
}

/* referencia→numero y observaciones→obs, igual que en el resto de tablas.
   Las líneas guardan su id real para poder actualizar "servida" línea a
   línea al recibir el pedido. */
async function cargarPedidos(){
  const { data, error } = await supabase.from('pedidos').select('*, pedido_lineas(id, precio, cantidad, servida)').order('fecha', {ascending:false});
  if(error){ db.pedidos=[]; return; }
  db.pedidos = data.map(p=>({id:p.id, contrato:p.contrato, numero:p.referencia, fecha:p.fecha, solicitante:p.solicitante,
    destino:p.destino, carretera:p.carretera, pk:p.pk, estado:p.estado, prevista:p.prevista, entrega:p.entrega,
    imputado:p.imputado, obs:p.observaciones, obsEstado:p.observaciones_estado, lineas:p.pedido_lineas||[]}));
}

/* emitida→fecha; no hay columna "estado" en la tabla porque en este
   prototipo una certificación nace y queda "emitida" para siempre. */
async function cargarCertificaciones(){
  const { data, error } = await supabase.from('certificaciones').select('*').order('emitida', {ascending:false});
  if(error){ db.certificaciones=[]; return; }
  db.certificaciones = data.map(c=>({id:c.id, contrato:c.contrato, numero:c.numero, desde:c.desde, hasta:c.hasta,
    base:c.base, gg:c.gastos_generales, bi:c.beneficio_industrial, iva:c.iva, total:c.total, fecha:c.emitida, estado:'emitida'}));
}

async function cargarContactos(){
  const { data, error } = await supabase.from('contactos').select('*').order('nombre');
  if(error){ db.contactos=[]; return; }
  db.contactos = data;
}

/* subido_por→usuario, subido_en→fecha (solo la parte de fecha). El fichero
   real vive en R2; aquí solo se guarda "ruta" (su clave en el bucket) —
   la miniatura/URL se pide al vuelo al pulsar "Ver" (ver-doc), no aquí. */
async function cargarDocumentos(){
  const { data, error } = await supabase.from('documentos').select('*').order('subido_en', {ascending:false});
  if(error){ db.documentos=[]; return; }
  db.documentos = data.map(d=>({id:d.id, contrato:d.contrato, pedido:d.pedido, tipo:d.tipo, nombre:d.nombre,
    fecha:d.subido_en.slice(0,10), tam:d.tam, usuario:d.subido_por, ruta:d.ruta}));
}

/* El fichero viaja dentro de esta misma petición a la Edge Function (que lo
   reenvía a R2 con sus credenciales) en vez de hacer el navegador un PUT
   directo a r2.cloudflarestorage.com: algunas redes corporativas (la de la
   Junta, por ejemplo) bloquean ese dominio y el PUT fallaba con "Failed to
   fetch". Devuelve el motivo del fallo (o null si fue bien) para poder decir
   en pantalla qué ha pasado, no solo "0 de 1". */
async function subirDocumento(file, cid, pedido, tipo){
  try{
    const form = new FormData();
    form.append('contrato', cid);
    form.append('file', file);
    const { data:subida, error:errSubida } = await supabase.functions.invoke('r2-firma', {body:form});
    if(errSubida || subida?.error){ console.error('r2-firma subir', errSubida||subida.error); return subida?.error||errSubida.message; }
    const { error } = await supabase.from('documentos').insert({contrato:cid, pedido, tipo,
      nombre:file.name, ruta:subida.ruta, tam:file.size, subido_por:S.usuario.id});
    if(error) console.error('insert documentos', error);
    return error?.message||null;
  } catch(e){ console.error('subirDocumento', e); return String(e); }
}

/* Igual que subirDocumento: pedirlo por la propia Edge Function en vez de
   una URL firmada, para que el navegador nunca conecte directo con
   r2.cloudflarestorage.com. La usan tanto "Ver" (abre en pestaña) como
   "Descargar" (guarda en disco). */
async function obtenerDocumento(d){
  const { data, error, response } = await supabase.functions.invoke('r2-firma', {body:{accion:'ver', ruta:d.ruta}});
  if(error) return { error: error.message };
  return { blob: new Blob([data], {type:response.headers.get('x-doc-type')||'application/octet-stream'}) };
}

/* =====================================================================
   EVENTOS
   ===================================================================== */
export function conectarEventos(){
document.addEventListener('click', async e=>{
  const el = e.target.closest('[data-act]');
  if(!el) return;
  const a = el.dataset.act;
  if(a==='cerrar-fuera' && e.target!==el) return;

  switch(a){
    case 'login': {
      const email = val('em'), pw = val('pw');
      if(!email||!pw){ toast('Escribe tu correo y contraseña'); return; }
      const { data, error } = await supabase.auth.signInWithPassword({email, password:pw});
      if(error){ toast('Correo o contraseña incorrectos'); return; }
      await cargarUsuarios();
      const perfil = db.usuarios.find(u=>u.id===data.user.id);
      if(!perfil?.activo){ toast('Tu cuenta no tiene acceso a GesCon todavía'); await supabase.auth.signOut(); db.usuarios=[]; return; }
      S.usuario = perfil;
      await Promise.all([cargarCatalogo('zonas'), cargarCatalogo('carreteras'), cargarCatalogo('almacenes'), cargarAjustes(), cargarContratos(), cargarPrecios(), cargarImputaciones(), cargarPedidos(), cargarCertificaciones(), cargarContactos(), cargarDocumentos()]);
      S.vista = S.usuario.rol==='encargado'?'pedidos':'panel'; S.contrato=null; render(); break;
    }
    case 'salir': await supabase.auth.signOut(); db.usuarios=[]; S.usuario=null; S.contrato=null; S.modal=null; render(); break;
    case 'mi-password': S.modal={t:'miPassword'}; render(); break;
    case 'guardar-mi-password': {
      const pw = val('mp-pass');
      if(!pw || pw.length<6){ toast('La contraseña necesita al menos 6 caracteres'); return; }
      const { error } = await supabase.auth.updateUser({password:pw});
      if(error){ toast('No se pudo cambiar: '+error.message); return; }
      S.modal=null; render(); toast('Contraseña actualizada'); break;
    }
    case 'ir': S.vista=el.dataset.v; S.contrato=null; render(); window.scrollTo(0,0); break;
    case 'filtro': S.filtro=el.dataset.f; render(); break;
    case 'filtro-ped': S.filtroPed=el.dataset.f; render(); break;
    case 'filtro-doc': S.tipoDoc=el.dataset.f; render(); break;
    case 'ordenar-imp': {
      const campo = el.dataset.campo;
      if(S.impOrden===campo) S.impOrdenDir = S.impOrdenDir==='asc'?'desc':'asc';
      else { S.impOrden=campo; S.impOrdenDir='asc'; }
      render(); break;
    }
    case 'tab': S.tab=el.dataset.t; S.tipoDoc='todos'; render(); window.scrollTo(0,0); break;
    case 'abrir-contrato': S.vista='contratos'; S.contrato=+el.dataset.id; S.tab='resumen'; render(); window.scrollTo(0,0); break;
    case 'volver-contratos': S.contrato=null; render(); break;
    case 'cerrar': case 'cerrar-fuera': S.modal=null; render(); break;

    case 'nueva-imputacion': S.modal={t:'imputacion', c:el.dataset.c?+el.dataset.c:S.contrato}; render(); break;
    case 'nuevo-contrato': S.modal={t:'contrato', id:null}; render(); break;
    case 'editar-contrato': S.modal={t:'contrato', id:+el.dataset.id}; render(); break;
    case 'editar-anualidades': S.modal={t:'anualidades', id:+el.dataset.id}; render(); break;
    case 'nueva-unidad': S.modal={t:'unidad', c:+el.dataset.c, id:null}; render(); break;
    case 'editar-unidad': S.modal={t:'unidad', c:null, id:+el.dataset.id}; render(); break;
    case 'nuevo-usuario': S.modal={t:'usuario', id:null}; render(); break;
    case 'editar-usuario': S.modal={t:'usuario', id:el.dataset.id}; render(); break;
    case 'nuevo-catalogo': S.modal={t:'catalogo', tipo:el.dataset.tipo, id:null}; render(); break;
    case 'editar-catalogo': S.modal={t:'catalogo', tipo:el.dataset.tipo, id:+el.dataset.id}; render(); break;
    case 'nuevo-contacto': S.modal={t:'contacto', c:+el.dataset.c, id:null}; render(); break;
    case 'editar-contacto': S.modal={t:'contacto', c:+el.dataset.c, id:+el.dataset.id}; render(); break;
    case 'nuevo-pedido': S.modal={t:'pedido', c:el.dataset.c?+el.dataset.c:S.contrato}; render(); break;
    case 'ver-pedido': S.modal={t:'verPedido', id:+el.dataset.id}; render(); break;
    case 'editar-pedido': { const p=pedidoDe(+el.dataset.id); S.modal={t:'pedido', c:p.contrato, editId:p.id}; render(); break; }
    case 'nueva-certificacion': S.modal={t:'certificacion', c:el.dataset.c?+el.dataset.c:S.contrato}; render(); break;
    case 'confirmar-anular': S.modal={t:'confirmarAnular', id:+el.dataset.id}; render(); break;
    case 'confirmar-borrar-contrato': S.modal={t:'confirmarBorrarContrato', id:+el.dataset.id}; render(); break;
    case 'borrar-contrato': {
      const id = +el.dataset.id;
      const { error } = await supabase.from('contratos').delete().eq('id', id);
      if(error){ toast('No se puede eliminar: tiene imputaciones, pedidos o certificaciones asociadas'); return; }
      await cargarContratos();
      S.modal=null; S.vista='contratos'; S.contrato=null; render(); toast('Contrato eliminado'); break;
    }
    case 'subir': { const inp=document.getElementById('subida'); inp.dataset.c=el.dataset.c; inp.dataset.p=el.dataset.p||''; inp.click(); break; }

    case 'plantilla-excel': descargarPlantilla(); break;
    case 'importar-excel': {
      const inp=document.getElementById('subida-excel'); inp.dataset.c=el.dataset.c; inp.click(); break;
    }
    case 'alternar-pisar': S.modal.pisar = !S.modal.pisar; render(); break;
    case 'confirmar-import': {
      const cid = +el.dataset.c, m = S.modal;
      const nuevas = m.filas.filter(f=>f.estado==='nueva');
      const cambios = m.pisar ? m.filas.filter(f=>f.estado==='actu') : [];
      if(nuevas.length) await supabase.from('precios').insert(
        nuevas.map(f=>({contrato:cid, codigo:f.codigo, descripcion:f.desc, ud:f.ud, precio:f.precio, capitulo:f.capitulo||null})));
      for(const f of cambios) await supabase.from('precios').update({descripcion:f.desc, ud:f.ud, precio:f.precio, capitulo:f.capitulo||null}).eq('id', f.idExistente);
      await cargarPrecios();
      S.modal=null; render();
      toast(`${nuevas.length} unidad${nuevas.length===1?'':'es'} añadida${nuevas.length===1?'':'s'}${cambios.length?` y ${cambios.length} actualizada${cambios.length===1?'':'s'}`:''}`);
      break;
    }

    case 'descargar': toast('En la versión final se genera el fichero: '+el.dataset.t); break;
    case 'validar-todo': {
      const { error } = await supabase.from('imputaciones').update({estado:'validada'}).eq('estado', 'borrador');
      if(error){ toast('No se pudo validar: '+error.message); return; }
      await cargarImputaciones();
      render(); toast('Imputaciones validadas'); break;
    }

    case 'add-linea-busqueda': {
      const pid = +el.dataset.precio, precio = precioDe(pid);
      const existente = document.querySelector(`#lineas tr[data-precio="${pid}"]`);
      if(existente){
        const inp = existente.querySelector('.lp-cant');
        inp.value = (parseFloat(inp.value)||0) + 1;
        actualizarLineaPedido(inp);
      } else {
        document.getElementById('lineas').insertAdjacentHTML('beforeend', filaLineaPedido(precio, 1));
      }
      actualizarTotalPedido();
      const busca = document.getElementById('p-busca');
      busca.value = ''; document.getElementById('p-resultados').innerHTML = ''; busca.focus();
      break;
    }
    case 'quitar-linea-ped': {
      el.closest('tr').remove();
      actualizarTotalPedido();
      break;
    }

    case 'add-linea-imp': {
      const cid = +val('i-c'), filas = document.querySelectorAll('#lineas-imp .linea-imp');
      const ultima = filas[filas.length-1];
      const base = ultima ? {precio:ultima.querySelector('.li-precio').value, fecha:fechaDeFila(ultima),
        carretera:ultima.querySelector('.li-carr').value} : {};
      document.getElementById('lineas-imp').insertAdjacentHTML('beforeend', lineaImputacionHTML(db.precios.filter(p=>p.contrato===cid), base));
      // Precio, fecha y carretera se heredan de la línea anterior, pero la
      // fecha es lo primero que suele cambiar entre líneas, así que el foco
      // va ahí (día) en vez de a medición.
      const nuevas = document.querySelectorAll('#lineas-imp .linea-imp');
      nuevas[nuevas.length-1].querySelector('.li-d').focus();
      break;
    }
    case 'quitar-linea-imp': {
      const cont = document.getElementById('lineas-imp');
      if(cont.querySelectorAll('.linea-imp').length>1) el.closest('.linea-imp').remove();
      else toast('Necesitas al menos una línea');
      break;
    }
    case 'elegir-precio-imp': {
      const fila = el.closest('.linea-imp'), precio = precioDe(+el.dataset.precio);
      fila.querySelector('.li-precio').value = precio.id;
      fila.querySelector('.li-busca').value = `${precio.codigo} · ${precio.desc}`;
      fila.querySelector('.li-ud').textContent = precio.ud;
      fila.querySelector('.li-resultados').innerHTML = '';
      break;
    }

    case 'guardar-imputacion': {
      const cid = +val('i-c'), lineas = [];
      document.querySelectorAll('#lineas-imp .linea-imp').forEach(r=>{
        const pid=+r.querySelector('.li-precio').value, q=parseFloat(r.querySelector('.li-cant').value);
        if(pid&&q>0) lineas.push({precio:pid, fecha:fechaDeFila(r),
          cantidad:q, carretera:r.querySelector('.li-carr').value, pk:pkDeFila(r),
          obs:r.querySelector('.li-obs').value});
      });
      if(!lineas.length){ toast('Añade al menos una línea con medición'); return; }
      const filas = lineas.map(l=>({contrato:cid, precio:l.precio, precio_unitario:precioDe(l.precio).precio,
        cantidad:l.cantidad, fecha:l.fecha, carretera:l.carretera, pk:l.pk, observaciones:l.obs,
        estado:'borrador', autor:S.usuario.id}));
      const { error } = await supabase.from('imputaciones').insert(filas);
      if(error){ toast('No se pudo registrar: '+error.message); return; }
      await cargarImputaciones();
      S.modal=null; render();
      toast(`${lineas.length} trabajo${lineas.length===1?'':'s'} registrado${lineas.length===1?'':'s'} · ${eur(lineas.reduce((s,l)=>s+l.cantidad*precioDe(l.precio).precio,0))}`);
      break;
    }

    case 'guardar-contrato': {
      const id = el.dataset.id ? +el.dataset.id : null;
      const exp=val('c-e'), nom=val('c-n'), imp=parseFloat(val('c-a')), fin=val('c-f');
      if(!exp||!nom){ toast('Faltan el expediente y el objeto del contrato'); return; }
      if(!(imp>0)){ toast('Indica el importe adjudicado'); return; }
      if(!fin){ toast('Indica el fin de vigencia'); return; }
      const datos = {expediente:exp, nombre:nom, empresa:val('c-emp')||'Pendiente de adjudicar', cif:val('c-cif')||'—',
        tipo:val('c-t'), adjudicado:imp, baja:parseFloat(val('c-b'))||0, inicio:val('c-i'), fin,
        prorroga:val('c-p')==='si', estado:val('c-est')};
      if(id){
        const { error } = await supabase.from('contratos').update(datos).eq('id', id);
        if(error){ toast('No se pudo actualizar: '+error.message); return; }
        await cargarContratos();
        S.modal=null; render(); toast('Contrato actualizado');
      } else {
        const an=[], y=anioActual;
        const y1=parseFloat(val('c-y1')), y2=parseFloat(val('c-y2'));
        if(y1>0) an.push({anio:y, importe:y1});
        if(y2>0) an.push({anio:y+1, importe:y2});
        if(!an.length) an.push({anio:y, importe:imp});
        const { data:nuevo, error } = await supabase.from('contratos')
          .insert({...datos, iva:db.ajustes.iva, gastos_generales:db.ajustes.gastosGenerales,
            beneficio_industrial:db.ajustes.beneficioIndustrial, contacto:''}).select().single();
        if(error){ toast('No se pudo dar de alta: '+error.message); return; }
        await supabase.from('anualidades').insert(an.map(a=>({contrato:nuevo.id, ...a})));
        await cargarContratos();
        S.modal=null; S.vista='contratos'; S.contrato=nuevo.id; S.tab='precios'; render();
        toast('Contrato dado de alta · ahora carga su cuadro de precios');
      }
      break;
    }

    case 'guardar-anualidades': {
      const cid=+el.dataset.id, an=[];
      document.querySelectorAll('.anualidad').forEach(i=>{
        const v=parseFloat(i.value); if(v>0) an.push({anio:+i.dataset.anio, importe:v});
      });
      an.sort((a,b)=>a.anio-b.anio);
      await supabase.from('anualidades').delete().eq('contrato', cid);
      if(an.length) await supabase.from('anualidades').insert(an.map(a=>({contrato:cid, ...a})));
      await cargarContratos();
      S.modal=null; render(); toast('Anualidades actualizadas'); break;
    }

    case 'guardar-unidad': {
      const id = el.dataset.id ? +el.dataset.id : null;
      const cid = +el.dataset.c;
      const cod=val('u-cod'), desc=val('u-desc'), pre=parseFloat(val('u-pre'));
      if(!cod||!desc){ toast('Faltan el código y la descripción'); return; }
      if(!(pre>0)){ toast('Indica un precio unitario'); return; }
      const fila = {codigo:cod, descripcion:desc, ud:val('u-ud'), precio:pre};
      const { error } = id
        ? await supabase.from('precios').update(fila).eq('id', id)
        : await supabase.from('precios').insert({...fila, contrato:cid});
      if(error){ toast('No se pudo guardar: '+error.message); return; }
      await cargarPrecios();
      S.modal=null; render(); toast(id?'Unidad actualizada':'Unidad añadida al cuadro de precios'); break;
    }

    case 'guardar-usuario': {
      const id = el.dataset.id || null;
      const nom=val('u-nom'), mail=val('u-mail');
      if(!nom){ toast('Falta el nombre'); return; }
      if(!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(mail)){ toast('El correo no tiene buena pinta'); return; }
      if(db.usuarios.some(u=>u.email.toLowerCase()===mail.toLowerCase() && u.id!==id)){ toast('Ya hay un usuario con ese correo'); return; }
      const todos = document.getElementById('u-todos').checked;
      const sel = [...document.querySelectorAll('.u-con:checked')].map(i=>+i.value);
      if(!todos && !sel.length){ toast('Asigna al menos un contrato o marca "todos"'); return; }
      const perfil = {nombre:nom, email:mail, rol:val('u-rol'), area:val('u-area')||'—',
                       activo:document.getElementById('u-activo').checked, todos_contratos:todos};
      if(id){
        const pwNueva = id===S.usuario.id ? val('u-passnueva') : '';
        if(pwNueva && pwNueva.length<6){ toast('La contraseña necesita al menos 6 caracteres'); return; }
        const { error } = await supabase.from('perfiles').update(perfil).eq('id', id);
        if(error){ toast('No se pudo actualizar: '+error.message); return; }
        await supabase.from('usuario_contrato').delete().eq('perfil', id);
        if(!todos) await supabase.from('usuario_contrato').insert(sel.map(cid=>({perfil:id, contrato:cid})));
        if(pwNueva){
          const { error: errPw } = await supabase.auth.updateUser({password:pwNueva});
          if(errPw){ toast('Usuario actualizado, pero la contraseña no cambió: '+errPw.message); S.modal=null; render(); await cargarUsuarios(); return; }
        }
        await cargarUsuarios();
        if(id===S.usuario.id) S.usuario=usuarioDe(id);
        S.modal=null; render(); toast(pwNueva?'Usuario y contraseña actualizados':'Usuario actualizado');
      } else {
        const pw = val('u-pass');
        if(!pw || pw.length<6){ toast('La contraseña necesita al menos 6 caracteres'); return; }
        const { data, error } = await supabase.functions.invoke('crear-usuario',
          {body:{nombre:nom, email:mail, rol:val('u-rol'), area:val('u-area')||'—', password:pw, todosContratos:todos, contratos:sel}});
        if(error || data?.error){ toast('No se pudo dar de alta: '+(data?.error||error.message)); return; }
        await cargarUsuarios();
        S.modal={t:'simple', tit:`${nom} dado de alta`,
          msg:`Correo: ${mail} · Contraseña provisional: ${pw}. Comunícaselo de palabra; puede cambiarla luego.`};
        render();
      }
      break;
    }

    case 'guardar-catalogo': {
      const tipo = el.dataset.tipo, id = el.dataset.id ? +el.dataset.id : null;
      const nom = val('k-nom');
      if(!nom){ toast('Falta el nombre'); return; }
      if(db[tipo].some(x=>x.nombre.toLowerCase()===nom.toLowerCase() && x.id!==id)){ toast('Ya existe con ese nombre'); return; }
      const { error } = id
        ? await supabase.from(tipo).update({nombre:nom}).eq('id', id)
        : await supabase.from(tipo).insert({nombre:nom});
      if(error){ toast('No se pudo guardar: '+error.message); return; }
      await cargarCatalogo(tipo);
      S.modal=null; render(); toast(id?'Actualizado':'Añadido'); break;
    }

    case 'guardar-tipos': {
      const iva=parseFloat(val('aj-iva')), bi=parseFloat(val('aj-bi')), gg=parseFloat(val('aj-gg'));
      if(!(iva>=0)||!(bi>=0)||!(gg>=0)){ toast('Los tres porcentajes tienen que ser números válidos'); return; }
      const { error } = await supabase.from('ajustes')
        .update({iva, beneficio_industrial:bi, gastos_generales:gg}).eq('id', 1);
      if(error){ toast('No se pudo guardar: '+error.message); return; }
      Object.assign(db.ajustes, {iva, beneficioIndustrial:bi, gastosGenerales:gg});
      render(); toast('Tipos impositivos actualizados'); break;
    }

    case 'borrar-catalogo': {
      const tipo = el.dataset.tipo, id = +el.dataset.id;
      const { error } = await supabase.from(tipo).delete().eq('id', id);
      if(error){ toast('No se pudo eliminar: '+error.message); return; }
      await cargarCatalogo(tipo);
      render(); toast('Eliminado'); break;
    }

    case 'guardar-contacto': {
      const cid = +el.dataset.c, id = el.dataset.id ? +el.dataset.id : null;
      const nom = val('ct-nom');
      if(!nom){ toast('Falta el nombre'); return; }
      const datos = {nombre:nom, cargo:val('ct-cargo'), telefono:val('ct-tel'), email:val('ct-mail'), obs:val('ct-obs')};
      const { error } = id
        ? await supabase.from('contactos').update(datos).eq('id', id)
        : await supabase.from('contactos').insert({...datos, contrato:cid});
      if(error){ toast('No se pudo guardar: '+error.message); return; }
      await cargarContactos();
      S.modal=null; render(); toast(id?'Contacto actualizado':'Contacto añadido'); break;
    }

    case 'borrar-contacto': {
      const id = +el.dataset.id, nom = db.contactos.find(c=>c.id===id).nombre;
      const { error } = await supabase.from('contactos').delete().eq('id', id);
      if(error){ toast('No se pudo eliminar: '+error.message); return; }
      await cargarContactos();
      render(); toast(`${nom} eliminado`); break;
    }

    case 'alternar-usuario': {
      const u = usuarioDe(el.dataset.id);
      if(u.id===S.usuario.id){ toast('No puedes desactivar tu propio usuario'); return; }
      const { error } = await supabase.from('perfiles').update({activo:!u.activo}).eq('id', u.id);
      if(error){ toast('No se pudo cambiar: '+error.message); return; }
      u.activo = !u.activo;
      if(S.modal?.t==='usuario') S.modal=null;
      render(); toast(u.activo?`${u.nombre} reactivado`:`${u.nombre} desactivado · sus registros se conservan`); break;
    }

    case 'guardar-pedido': {
      const cid=+val('p-c'), numero=val('p-num'), dest=val('p-dest');
      if(!numero){ toast('Falta el número de pedido'); return; }
      if(!dest){ toast('Indica el destino del pedido'); return; }
      const lineas=[];
      document.querySelectorAll('#lineas tr[data-precio]').forEach(r=>{
        const pid=+r.dataset.precio, q=parseFloat(r.querySelector('.lp-cant').value);
        if(pid&&q>0) lineas.push({precio:pid, cantidad:q, servida:0});
      });
      if(!lineas.length){ toast('Añade al menos una línea con cantidad'); return; }
      const { data:nuevo, error } = await supabase.from('pedidos').insert({contrato:cid, referencia:numero,
        fecha:val('p-f'), solicitante:S.usuario.id, destino:dest, carretera:val('p-carr'), pk:val('p-pk')||'—',
        estado:val('p-est'), prevista:val('p-prev')||null, observaciones:val('p-obs')}).select().single();
      if(error){ toast('No se pudo registrar: '+error.message); return; }
      await supabase.from('pedido_lineas').insert(lineas.map(l=>({pedido:nuevo.id, precio:l.precio, cantidad:l.cantidad})));
      const adjuntos = [...document.getElementById('p-docs').files];
      for(const f of adjuntos) await subirDocumento(f, cid, nuevo.id, sugerirTipo(f.name));
      await Promise.all([cargarPedidos(), cargarDocumentos()]);
      S.modal=null; render();
      toast(`Pedido ${numero} registrado${adjuntos.length?` con ${adjuntos.length} documento${adjuntos.length===1?'':'s'}`:''} · ${eur(lineas.reduce((s,l)=>s+l.cantidad*precioDe(l.precio).precio,0))}`); break;
    }

    case 'guardar-edicion-pedido': {
      const pid = +el.dataset.id, p = pedidoDe(pid);
      if(p.estado!=='solicitado'){ toast('Ya no se puede editar: el pedido ha cambiado de estado'); return; }
      const numero=val('p-num'), dest=val('p-dest');
      if(!numero){ toast('Falta el número de pedido'); return; }
      if(!dest){ toast('Indica el destino del pedido'); return; }
      const lineas=[];
      document.querySelectorAll('#lineas tr[data-precio]').forEach(r=>{
        const precio=+r.dataset.precio, cantidad=parseFloat(r.querySelector('.lp-cant').value);
        if(precio&&cantidad>0) lineas.push({id:r.dataset.linea?+r.dataset.linea:null, precio, cantidad});
      });
      if(!lineas.length){ toast('Añade al menos una línea con cantidad'); return; }
      const { error } = await supabase.from('pedidos').update({referencia:numero, fecha:val('p-f'),
        destino:dest, carretera:val('p-carr'), pk:val('p-pk')||'—', prevista:val('p-prev')||null,
        observaciones:val('p-obs')}).eq('id', pid);
      if(error){ toast('No se pudo guardar: '+error.message); return; }
      const idsActuales = lineas.filter(l=>l.id).map(l=>l.id);
      const borradas = p.lineas.filter(l=>!idsActuales.includes(l.id));
      if(borradas.length) await supabase.from('pedido_lineas').delete().in('id', borradas.map(l=>l.id));
      await Promise.all(lineas.map(l => l.id
        ? supabase.from('pedido_lineas').update({precio:l.precio, cantidad:l.cantidad}).eq('id', l.id)
        : supabase.from('pedido_lineas').insert({pedido:pid, precio:l.precio, cantidad:l.cantidad})));
      const adjuntos = [...document.getElementById('p-docs').files];
      for(const f of adjuntos) await subirDocumento(f, p.contrato, pid, sugerirTipo(f.name));
      await Promise.all([cargarPedidos(), cargarDocumentos()]);
      S.modal={t:'verPedido', id:pid}; render();
      toast(`Pedido ${numero} actualizado`); break;
    }

    case 'guardar-obs-estado': {
      const p = pedidoDe(+el.dataset.id);
      if(p.solicitante!==S.usuario.id){ toast('Solo quien hizo el pedido puede añadir esta observación'); return; }
      const texto = val('p-obs-estado');
      const { error } = await supabase.from('pedidos').update({observaciones_estado:texto}).eq('id', p.id);
      if(error){ toast('No se pudo guardar: '+error.message); return; }
      await cargarPedidos();
      render(); toast('Observación guardada'); break;
    }

    case 'guardar-recepcion': {
      const p = pedidoDe(+el.dataset.id);
      if(el.dataset.modo==='total'){ p.lineas.forEach(l=>l.servida=l.cantidad); }
      else { document.querySelectorAll('.lp-serv').forEach(i=>{
        const l=p.lineas[+i.dataset.ix]; l.servida=Math.max(0, Math.min(parseFloat(i.value)||0, l.cantidad)); }); }
      const todo = p.lineas.every(l=>l.servida>=l.cantidad);
      const algo = p.lineas.some(l=>l.servida>0);
      const estado = todo ? 'servido' : algo ? 'parcial' : (p.estado==='parcial'?'confirmado':p.estado);
      const entrega = (todo && !p.entrega) ? hoyISO() : p.entrega;
      await Promise.all(p.lineas.map(l=>supabase.from('pedido_lineas').update({servida:l.servida}).eq('id', l.id)));
      const { error } = await supabase.from('pedidos').update({estado, entrega}).eq('id', p.id);
      if(error){ toast('No se pudo guardar la recepción: '+error.message); return; }
      await cargarPedidos();
      S.modal = todo ? {t:'verPedido', id:p.id} : null;
      render(); toast(todo?`${p.numero} servido · ya puedes imputarlo`:'Recepción parcial guardada'); break;
    }

    case 'anular-pedido': {
      const p = pedidoDe(+el.dataset.id);
      const { error } = await supabase.from('pedidos').update({estado:'anulado'}).eq('id', p.id);
      if(error){ toast('No se pudo anular: '+error.message); return; }
      await cargarPedidos();
      S.modal=null; render(); toast(`${p.numero} anulado`); break;
    }

    case 'imputar-pedido': {
      const p = pedidoDe(+el.dataset.id);
      const filas = p.lineas.filter(l=>l.servida>0).map(l=>({contrato:p.contrato, precio:l.precio,
        precio_unitario:precioDe(l.precio).precio, cantidad:l.servida, fecha:p.entrega||hoyISO(),
        carretera:p.carretera||p.destino, pk:p.pk, destino:p.destino, estado:'validada', autor:S.usuario.id, pedido:p.id}));
      if(filas.length){
        const { error } = await supabase.from('imputaciones').insert(filas);
        if(error){ toast('No se pudo imputar: '+error.message); return; }
      }
      const { error: errPed } = await supabase.from('pedidos').update({imputado:true}).eq('id', p.id);
      if(errPed){ toast('No se pudo marcar como imputado: '+errPed.message); return; }
      await Promise.all([cargarPedidos(), cargarImputaciones()]);
      S.modal=null; render();
      toast(`${filas.length} líneas imputadas al contrato · ${eur(filas.reduce((s,f)=>s+f.cantidad*f.precio_unitario,0))}`); break;
    }

    case 'guardar-certificacion': {
      const cid=+val('k-c'), d=val('k-d'), h=val('k-h');
      const inc = db.imputaciones.filter(i=>i.contrato===cid && i.estado!=='certificada' && i.fecha>=d && i.fecha<=h);
      if(!inc.length){ toast('No hay imputaciones en ese periodo'); return; }
      const n = db.certificaciones.filter(x=>x.contrato===cid).length+1;
      const ct = contratoDe(cid);
      // Los precios del cuadro son de ejecución material: hay que sumar
      // gastos generales y beneficio industrial antes de aplicar el IVA.
      const base = inc.reduce((s,i)=>s+importeDe(i),0);
      const gg = base*ct.gastosGenerales/100, bi = base*ct.beneficioIndustrial/100;
      const iva = (base+gg+bi)*ct.iva/100, total = base+gg+bi+iva;
      const { data:nueva, error } = await supabase.from('certificaciones')
        .insert({contrato:cid, numero:n, desde:d, hasta:h, base,
          gastos_generales:gg, beneficio_industrial:bi, iva, total, autor:S.usuario.id}).select().single();
      if(error){ toast('No se pudo emitir: '+error.message); return; }
      // Certificada de verdad: se enlaza con esta certificación y ya no se
      // vuelve a tocar (regla del negocio: lo certificado no se modifica).
      const { error: errImp } = await supabase.from('imputaciones')
        .update({estado:'certificada', certificacion:nueva.id}).in('id', inc.map(i=>i.id));
      if(errImp) toast('Certificación emitida, pero no se pudieron cerrar todas las imputaciones: '+errImp.message);
      await Promise.all([cargarCertificaciones(), cargarImputaciones()]);
      S.modal=null; S.vista='certificaciones'; S.contrato=null; render();
      toast(`Certificación nº ${n} emitida · ${eur(total)}`); break;
    }

    case 'guardar-docs': {
      const cid=+el.dataset.c, ped=val('d-ped');
      const tipos=[...document.querySelectorAll('.doc-tipo')].map(s=>s.value);
      const ficheros = S.modal.ficheros;
      let subidos = 0, ultimoError = null;
      for(let i=0;i<ficheros.length;i++){
        const err = await subirDocumento(ficheros[i].file, cid, ped?+ped:null, tipos[i]||'Otro');
        if(err) ultimoError = err; else subidos++;
      }
      await cargarDocumentos();
      const volverPedido = el.dataset.p ? +el.dataset.p : null;
      if(volverPedido){ S.modal = {t:'verPedido', id:volverPedido}; }
      else { S.modal=null; S.vista='contratos'; S.contrato=cid; S.tab='docs'; S.tipoDoc='todos'; }
      render();
      toast(subidos===ficheros.length ? `${subidos} ${subidos===1?'documento subido':'documentos subidos'}`
        : `${subidos} de ${ficheros.length} documentos subidos${ultimoError?' · '+ultimoError:''}`); break;
    }

    case 'ver-doc': {
      const d = db.documentos.find(x=>x.id===+el.dataset.id);
      const { blob, error } = await obtenerDocumento(d);
      if(error){ toast('No se pudo abrir: '+error); return; }
      window.open(URL.createObjectURL(blob), '_blank'); break;
    }

    case 'descargar-doc': {
      const d = db.documentos.find(x=>x.id===+el.dataset.id);
      const { blob, error } = await obtenerDocumento(d);
      if(error){ toast('No se pudo descargar: '+error); return; }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = d.nombre;
      document.body.appendChild(a); a.click(); a.remove();
      // Revocar la URL justo después del click puede cortar la descarga
      // antes de que el navegador termine de leerla.
      setTimeout(()=>URL.revokeObjectURL(url), 2000);
      toast(`Descargando ${d.nombre}`); break;
    }

    case 'confirmar-borrar-doc': S.modal={t:'confirmarBorrarDoc', id:+el.dataset.id, pedido:el.dataset.p?+el.dataset.p:null}; render(); break;

    case 'borrar-doc': {
      const id = +el.dataset.id, nom = db.documentos.find(d=>d.id===id).nombre;
      const volverPedido = el.dataset.p ? +el.dataset.p : null;
      const { data, error } = await supabase.functions.invoke('r2-firma', {body:{accion:'borrar', id}});
      if(error || data?.error){ toast('No se pudo eliminar: '+(data?.error||error.message)); return; }
      await cargarDocumentos();
      S.modal = volverPedido ? {t:'verPedido', id:volverPedido} : null;
      render(); toast(`${nom} eliminado`); break;
    }
  }
});

document.addEventListener('change', e=>{
  const el = e.target.closest('[data-act]');
  if(!el) return;
  const a = el.dataset.act;
  if(a==='cambiar-contrato-imp'){ S.modal={t:'imputacion', c:+el.value}; render(); }
  if(a==='cambiar-contrato-cert'){ S.modal={t:'certificacion', c:+el.value}; render(); }
  if(a==='cambiar-contrato-ped'){ S.modal={t:'pedido', c:+el.value}; render(); }
  if(a==='filtro-ped-contrato'){ S.filtroPedContrato = el.value==='todos'?'todos':+el.value; render(); }
  if(a==='filtro-cert-contrato'){ S.filtroCertContrato = el.value==='todos'?'todos':+el.value; render(); }
  if(a==='filtro-imp-contrato'){ S.filtroImpContrato = el.value==='todos'?'todos':+el.value; render(); }
  if(a==='filtro-imp-carretera'){ S.filtroImpCarretera = el.value; render(); }
  if(a==='filtro-imp-fecha'){ if(el.dataset.tipo==='desde') S.impDesde=el.value; else S.impHasta=el.value; renderTrasEditar(el); }
  if(a==='filtro-inf-contrato'){ S.infContrato = el.value==='todos'?'todos':+el.value; render(); }
  if(a==='filtro-inf-carretera'){ S.infCarretera = el.value; render(); }
  if(a==='filtro-inf-fecha'){ if(el.dataset.tipo==='desde') S.infDesde=el.value; else S.infHasta=el.value; renderTrasEditar(el); }
  if(a==='saltar-contrato'){ S.contrato=+el.value; S.tipoDoc='todos'; render(); window.scrollTo(0,0); }
  if(a==='alternar-todos'){ document.getElementById('u-lista').style.display = el.checked ? 'none' : ''; }
  if(a==='cambiar-rol'){
    if(el.value==='admin'||el.value==='consulta'){
      document.getElementById('u-todos').checked = true;
      document.getElementById('u-lista').style.display = 'none';
    }
  }
});

/* Las cajas de fecha llegan ya rellenas (p.ej. "06") con el hueco justo para
   2 dígitos: sin esto, el cursor cae detrás del texto y no queda sitio para
   teclear nada nuevo. Seleccionando todo al entrar, la primera pulsación lo
   sobrescribe entero, como un cuentakilómetros. El focus no burbujea: hace
   falta capture. */
document.addEventListener('focus', e=>{
  if(e.target.matches?.('.li-d,.li-m,.li-a')) e.target.select();
}, true);

document.addEventListener('input', e=>{
  // Buscador de unidad del pedido: filtra en vivo, sin pasar por render()
  // para no perder el foco en cada tecla (igual que la vista previa de
  // certificación o el reparto de anualidades).
  if(e.target.id==='p-busca'){
    const cid = +val('p-c');
    const resultados = filtrarPrecios(db.precios.filter(p=>p.contrato===cid), e.target.value);
    document.getElementById('p-resultados').innerHTML = resultados.map(p=>filaResultadoBusqueda(p,'add-linea-busqueda')).join('');
    return;
  }
  // Lo mismo, pero un buscador por cada línea de imputación.
  if(e.target.classList.contains('li-busca')){
    const fila = e.target.closest('.linea-imp'), cid = +val('i-c');
    const resultados = filtrarPrecios(db.precios.filter(p=>p.contrato===cid), e.target.value);
    fila.querySelector('.li-resultados').innerHTML = resultados.map(p=>filaResultadoBusqueda(p,'elegir-precio-imp')).join('');
    return;
  }
  if(e.target.classList.contains('lp-cant')){
    actualizarLineaPedido(e.target);
    actualizarTotalPedido();
    return;
  }
  // Al completar día o mes (2 dígitos) se salta solo a la siguiente caja de fecha.
  if(e.target.classList.contains('li-d')||e.target.classList.contains('li-m')){
    if(e.target.value.length<2) return;
    const cajas = [...e.target.closest('.li-fecha').querySelectorAll('input')];
    cajas[cajas.indexOf(e.target)+1]?.focus();
    return;
  }
  // PK como punto kilométrico: los 3 últimos dígitos son los metros.
  // "5300"→"5+300" · "344200"→"344+200" · "0500"→"0+500".
  if(e.target.classList.contains('li-pki')||e.target.classList.contains('li-pkf')){
    const digitos = e.target.value.replace(/\D/g,'');
    e.target.value = digitos.length>3 ? digitos.slice(0,-3)+'+'+digitos.slice(-3) : digitos;
  }
});

document.addEventListener('keydown', e=>{
  if(e.key==='Escape'&&S.modal){ S.modal=null; render(); return; }
  if(e.key!=='Enter') return;
  const fila = e.target.closest('.linea-imp');
  if(!fila) return;
  const campo = ORDEN_LINEA_IMP.find(c=>e.target.classList.contains(c));
  if(!campo) return;
  e.preventDefault();
  const i = ORDEN_LINEA_IMP.indexOf(campo);
  if(i<ORDEN_LINEA_IMP.length-1) fila.querySelector('.'+ORDEN_LINEA_IMP[i+1])?.focus();
  else document.querySelector('[data-act="add-linea-imp"]')?.click();
});
}
